import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import Button from '../components/Button';
import { fetchLiveResorts, fetchResortMapData } from '../services/resortLiveMapsService';
import { useError } from '../contexts/ErrorContext';
import { MAPBOX_STYLE } from '../constants/mapbox';
import './ResortLiveMapsPage.css';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

const STATUS_COLORS = {
  open: '#22c55e',
  closed: '#ef4444',
  unknown: '#6b7280',
};

const MAP_STYLES = {
  rekkie: { id: MAPBOX_STYLE, label: 'REKKIE' },
  dark: { id: 'mapbox://styles/mapbox/dark-v11', label: 'Dark' },
};

function formatUpdatedAt(updatedAt) {
  if (!updatedAt) return null;
  try {
    const d = updatedAt instanceof Date ? updatedAt : new Date(updatedAt);
    return d.toLocaleString();
  } catch {
    return null;
  }
}

const RING_SIZE = 72;
const RING_STROKE = 5;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const ChairliftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7" cy="4" r="2" />
    <line x1="2" y1="2" x2="22" y2="8" />
    <path d="M7 6v4l-3 8h2l2-4 3 4h2l-3-8V8" />
    <line x1="18" y1="6.5" x2="18" y2="18" />
    <rect x="16" y="18" width="4" height="2" rx="1" />
  </svg>
);

const TrailIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 20 C8 14, 10 18, 12 12 C14 6, 16 10, 20 4" />
  </svg>
);

const GroomingIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="6" width="18" height="12" rx="2" />
    <line x1="7" y1="10" x2="7" y2="14" />
    <line x1="10" y1="10" x2="10" y2="14" />
    <line x1="13" y1="10" x2="13" y2="14" />
    <line x1="16" y1="10" x2="16" y2="14" />
  </svg>
);

const StatusRing = ({ known, total, open, closed, label, icon }) => {
  const openPct = total > 0 ? open / total : 0;
  const closedPct = total > 0 ? closed / total : 0;
  const openDash = openPct * RING_CIRCUMFERENCE;
  const closedDash = closedPct * RING_CIRCUMFERENCE;
  const openOffset = -RING_CIRCUMFERENCE * 0.25; // start at top
  const closedOffset = openOffset - openDash;

  return (
    <div className="rlm-ring-stat">
      <div className="rlm-ring-wrap">
        <svg width={RING_SIZE} height={RING_SIZE}>
          {/* Background track */}
          <circle
            cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_RADIUS}
            fill="none" stroke="#222" strokeWidth={RING_STROKE}
          />
          {/* Unknown segment — sits behind, only visible where open/closed don't cover */}
          {total - known > 0 && (
            <circle
              cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_RADIUS}
              fill="none" stroke="#333" strokeWidth={RING_STROKE}
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={0}
            />
          )}
          {/* Open arc */}
          {open > 0 && (
            <circle
              cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_RADIUS}
              fill="none" stroke="#22c55e" strokeWidth={RING_STROKE}
              strokeLinecap="round"
              strokeDasharray={`${openDash} ${RING_CIRCUMFERENCE - openDash}`}
              strokeDashoffset={openOffset}
              className="rlm-ring-arc"
            />
          )}
          {/* Closed arc */}
          {closed > 0 && (
            <circle
              cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_RADIUS}
              fill="none" stroke="#ef4444" strokeWidth={RING_STROKE}
              strokeLinecap="round"
              strokeDasharray={`${closedDash} ${RING_CIRCUMFERENCE - closedDash}`}
              strokeDashoffset={closedOffset}
              className="rlm-ring-arc"
            />
          )}
        </svg>
        <div className="rlm-ring-inner">
          <div className="rlm-ring-icon">{icon}</div>
        </div>
      </div>
      <div className="rlm-ring-text">
        <span className="rlm-ring-fraction">
          <span className="rlm-ring-num">{known}</span>
          <span className="rlm-ring-slash">/{total}</span>
        </span>
        <span className="rlm-ring-label">{label}</span>
      </div>
    </div>
  );
};

const ResortLiveMapsPage = () => {
  const navigate = useNavigate();
  const { addError } = useError();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(null);

  const [resorts, setResorts] = useState([]);
  const [loadingResorts, setLoadingResorts] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedResort, setSelectedResort] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mapStyle, setMapStyle] = useState('rekkie');
  const [activeTab, setActiveTab] = useState('lifts'); // 'lifts' | 'trails'

  // Load available resorts
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchLiveResorts();
        if (!cancelled) {
          setResorts(data);
          setLoadingResorts(false);
        }
      } catch (err) {
        if (!cancelled) {
          addError(err.message, err.stack, 'Resort Live Maps');
          setLoadingResorts(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [addError]);

  const filteredResorts = searchQuery.trim()
    ? resorts.filter(r =>
        r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.region.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : resorts;

  const handleSelectResort = useCallback(async (resort) => {
    setSelectedResort(resort);
    setSearchQuery('');
    setMapData(null);
    setError(null);
    setLoading(true);
    try {
      const data = await fetchResortMapData(resort.id);
      setMapData(data);
    } catch (err) {
      setError(err.message);
      addError(err.message, err.stack, 'Resort Live Maps');
    } finally {
      setLoading(false);
    }
  }, [addError]);

  // Add GeoJSON layers to map
  const addMapLayers = useCallback((map, data) => {
    if (!data?.runsGeoJson) return;

    ['rlm-runs-open', 'rlm-runs-open-ungroomed', 'rlm-runs-closed', 'rlm-runs-unknown'].forEach(id => {
      if (map.getLayer(id)) map.removeLayer(id);
    });
    if (map.getSource('rlm-runs')) map.removeSource('rlm-runs');

    map.addSource('rlm-runs', { type: 'geojson', data: data.runsGeoJson });

    // Open runs — solid, bright
    map.addLayer({
      id: 'rlm-runs-open',
      type: 'line',
      source: 'rlm-runs',
      filter: ['==', ['get', 'operationalStatus'], 'open'],
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': [
          'case',
          ['==', ['get', 'groomingStatus'], 'groomed'], '#22c55e',
          '#4ade80',
        ],
        'line-width': 3,
        'line-opacity': 0.9,
      },
    });

    // Open + ungroomed — slightly different shade, dashed
    map.addLayer({
      id: 'rlm-runs-open-ungroomed',
      type: 'line',
      source: 'rlm-runs',
      filter: ['all', ['==', ['get', 'operationalStatus'], 'open'], ['!=', ['get', 'groomingStatus'], 'groomed']],
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': '#4ade80',
        'line-width': 2.5,
        'line-opacity': 0.8,
        'line-dasharray': [3, 2],
      },
    });

    // Closed runs
    map.addLayer({
      id: 'rlm-runs-closed',
      type: 'line',
      source: 'rlm-runs',
      filter: ['==', ['get', 'operationalStatus'], 'closed'],
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': '#ef4444',
        'line-width': 2,
        'line-opacity': 0.6,
      },
    });

    // Unknown / no data runs
    map.addLayer({
      id: 'rlm-runs-unknown',
      type: 'line',
      source: 'rlm-runs',
      filter: ['==', ['get', 'operationalStatus'], 'unknown'],
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': '#6b7280',
        'line-width': 1.5,
        'line-opacity': 0.4,
      },
    });

    // Hover popup
    const hoverLayers = ['rlm-runs-open', 'rlm-runs-closed', 'rlm-runs-unknown', 'rlm-runs-open-ungroomed'];
    hoverLayers.forEach(layerId => {
      map.on('mouseenter', layerId, (e) => {
        map.getCanvas().style.cursor = 'pointer';
        const props = e.features[0].properties;
        const status = props.operationalStatus;
        const grooming = props.groomingStatus;
        const statusColor = STATUS_COLORS[status] || STATUS_COLORS.unknown;
        const html = `<div style="font-size:12px;max-width:200px;">
          <div style="font-weight:600;margin-bottom:4px;">${props.name || 'Run'}</div>
          <div style="color:${statusColor};font-weight:500;">${status}</div>
          ${grooming && grooming !== 'unknown' ? `<div style="color:#aaa;margin-top:2px;">Grooming: ${grooming}</div>` : ''}
          ${props.colorName ? `<div style="color:#888;margin-top:2px;">${props.colorName}</div>` : ''}
        </div>`;
        if (popupRef.current) popupRef.current.remove();
        // Get first [lng, lat] pair regardless of geometry type
        let coords = e.features[0].geometry.coordinates;
        while (Array.isArray(coords) && Array.isArray(coords[0]) && Array.isArray(coords[0][0])) {
          coords = coords[0];
        }
        popupRef.current = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 10 })
          .setLngLat(coords[0])
          .setHTML(html)
          .addTo(map);
      });
      map.on('mouseleave', layerId, () => {
        map.getCanvas().style.cursor = '';
        if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }
      });
    });

    // Fit to runs bounds — recursively extract [lng, lat] pairs from any geometry type
    const allCoords = [];
    const extractCoords = (arr) => {
      if (!Array.isArray(arr)) return;
      // If first element is a number, this is a [lng, lat] coordinate
      if (typeof arr[0] === 'number') { allCoords.push(arr); return; }
      for (const item of arr) extractCoords(item);
    };
    for (const f of data.runsGeoJson.features) {
      if (f.geometry?.coordinates) extractCoords(f.geometry.coordinates);
    }
    if (allCoords.length > 0) {
      const bounds = allCoords.reduce(
        (b, c) => b.extend(c),
        new mapboxgl.LngLatBounds(allCoords[0], allCoords[0])
      );
      map.fitBounds(bounds, { padding: 60, maxZoom: 15 });
    }
  }, []);

  // Init map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLES[mapStyle].id,
      center: [0, 45],
      zoom: 3,
    });
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    mapRef.current = map;
    map.once('load', () => {
      if (mapData) addMapLayers(map, mapData);
    });
    return () => {
      if (popupRef.current) popupRef.current.remove();
      map.remove();
      mapRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResort, mapData]);

  // Add layers when mapData arrives
  useEffect(() => {
    if (!mapData || !mapRef.current) return;
    const map = mapRef.current;
    if (map.loaded()) addMapLayers(map, mapData);
    else map.once('load', () => addMapLayers(map, mapData));
  }, [mapData, addMapLayers]);

  const handleStyleToggle = useCallback(() => {
    const next = mapStyle === 'rekkie' ? 'dark' : 'rekkie';
    setMapStyle(next);
    const map = mapRef.current;
    if (!map) return;
    map.setStyle(MAP_STYLES[next].id);
    map.once('style.load', () => {
      if (mapData) addMapLayers(map, mapData);
    });
  }, [mapStyle, mapData, addMapLayers]);

  // Group lifts by status
  const liftGroups = React.useMemo(() => {
    if (!mapData) return { open: [], closed: [], unknown: [] };
    const sorted = [...mapData.lifts].sort((a, b) => (a.name || a.id || '').localeCompare(b.name || b.id || ''));
    return {
      open: sorted.filter(l => l.operationalStatus === 'open'),
      closed: sorted.filter(l => l.operationalStatus === 'closed'),
      unknown: sorted.filter(l => l.operationalStatus !== 'open' && l.operationalStatus !== 'closed'),
    };
  }, [mapData]);

  // Group trails by grooming status
  const trailGroups = React.useMemo(() => {
    if (!mapData?.runsGeoJson) return { groomed: [], ungroomed: [], unknown: [] };
    const all = mapData.runsGeoJson.features
      .map(f => f.properties)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return {
      groomed: all.filter(t => t.hasLiveData && t.groomingStatus === 'groomed'),
      ungroomed: all.filter(t => t.hasLiveData && t.groomingStatus !== 'groomed' && t.groomingStatus !== 'unknown'),
      unknown: all.filter(t => !t.hasLiveData || t.groomingStatus === 'unknown'),
    };
  }, [mapData]);

  const [collapsedSections, setCollapsedSections] = useState({});
  const toggleSection = useCallback((key) => {
    setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Build a lookup of run features by id for fly-to
  const runFeaturesById = React.useMemo(() => {
    if (!mapData?.runsGeoJson) return {};
    const map = {};
    for (const f of mapData.runsGeoJson.features) {
      if (f.properties?.id) map[f.properties.id] = f;
    }
    return map;
  }, [mapData]);

  const flyToLift = useCallback((lift) => {
    const map = mapRef.current;
    if (!map || !lift.coordinate) return;
    map.flyTo({ center: lift.coordinate, zoom: 15, duration: 1000 });
  }, []);

  const flyToRun = useCallback((trailId) => {
    const map = mapRef.current;
    const feature = runFeaturesById[trailId];
    if (!map || !feature?.geometry?.coordinates) return;
    // Compute bounds of the run
    const coords = [];
    const extract = (arr) => {
      if (!Array.isArray(arr)) return;
      if (typeof arr[0] === 'number') { coords.push(arr); return; }
      for (const item of arr) extract(item);
    };
    extract(feature.geometry.coordinates);
    if (coords.length === 0) return;
    const bounds = coords.reduce(
      (b, c) => b.extend(c),
      new mapboxgl.LngLatBounds(coords[0], coords[0])
    );
    map.fitBounds(bounds, { padding: 100, maxZoom: 16, duration: 1000 });
  }, [runFeaturesById]);

  return (
    <div className="rlm-page">
      <header className="rlm-header">
        <div className="rlm-header-content">
          <div className="rlm-header-left">
            <Button variant="outline" size="medium" onClick={() => navigate('/map-management')}>←</Button>
            <h1>{selectedResort ? selectedResort.name : 'Resort Live Maps'}</h1>
            {mapData?.updatedAt && (
              <span className="rlm-updated">Updated {formatUpdatedAt(mapData.updatedAt)}</span>
            )}
          </div>
          <div className="rlm-header-right">
            {selectedResort && (
              <Button variant="outline" size="small" onClick={() => { setSelectedResort(null); setMapData(null); setError(null); }}>
                Change Resort
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="rlm-main">
        {!selectedResort && (
          <div className="rlm-select-screen">
            <div className="rlm-select-inner">
              <h2>Select a Resort</h2>
              <p>Choose a resort to view live run and lift status</p>
              <div className="rlm-search-wrapper">
                <input
                  type="text"
                  className="rlm-search-input"
                  placeholder={loadingResorts ? 'Loading resorts...' : 'Search resorts...'}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  disabled={loadingResorts}
                  autoFocus
                />
              </div>
              <div className="rlm-resort-table-wrap">
                <table className="rlm-resort-table">
                  <thead>
                    <tr>
                      <th>Resort</th>
                      <th>Region</th>
                      <th>Last Run</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResorts.map(resort => (
                      <tr key={resort.id} className="rlm-resort-row" onClick={() => handleSelectResort(resort)}>
                        <td className="rlm-resort-name">{resort.name}</td>
                        <td className="rlm-resort-meta">{[resort.region, resort.country].filter(Boolean).join(', ')}</td>
                        <td className="rlm-resort-updated">{formatUpdatedAt(resort.updatedAt) || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!loadingResorts && filteredResorts.length === 0 && (
                  <div className="rlm-empty">No resorts found</div>
                )}
              </div>
            </div>
          </div>
        )}

        {selectedResort && loading && (
          <div className="rlm-loading">
            <div className="rlm-spinner" />
            <p>Loading live data for {selectedResort.name}…</p>
          </div>
        )}

        {selectedResort && error && (
          <div className="rlm-error">
            <p>{error}</p>
            <Button variant="outline" size="small" onClick={() => handleSelectResort(selectedResort)}>Retry</Button>
          </div>
        )}

        {selectedResort && mapData && !loading && (
          <div className="rlm-content">
            {/* Map */}
            <div className="rlm-map-container">
              <div ref={mapContainerRef} className="rlm-map" />

              {/* Stats rings */}
              <div className="rlm-stats-bar">
                <StatusRing
                  known={mapData.stats.liftsKnown}
                  total={mapData.stats.liftsTotal}
                  open={mapData.stats.liftsOpen}
                  closed={mapData.stats.liftsClosed || 0}
                  label="Lifts"
                  icon={<ChairliftIcon />}
                />
                <StatusRing
                  known={mapData.stats.trailsKnown}
                  total={mapData.stats.trailsTotal}
                  open={mapData.stats.trailsOpen}
                  closed={mapData.stats.trailsClosed || 0}
                  label="Runs"
                  icon={<TrailIcon />}
                />
                <StatusRing
                  known={mapData.stats.trailsGroomed}
                  total={mapData.stats.trailsKnown}
                  open={mapData.stats.trailsGroomed}
                  closed={0}
                  label="Groomed"
                  icon={<GroomingIcon />}
                />
              </div>

              {/* Legend */}
              <div className="rlm-legend">
                <div className="rlm-legend-item">
                  <div className="rlm-legend-line" style={{ background: '#22c55e' }} />
                  <span>Open (groomed)</span>
                </div>
                <div className="rlm-legend-item">
                  <div className="rlm-legend-line rlm-legend-dashed" style={{ borderColor: '#4ade80' }} />
                  <span>Open (ungroomed)</span>
                </div>
                <div className="rlm-legend-item">
                  <div className="rlm-legend-line" style={{ background: '#ef4444' }} />
                  <span>Closed</span>
                </div>
                <div className="rlm-legend-item">
                  <div className="rlm-legend-line" style={{ background: '#6b7280', opacity: 0.5 }} />
                  <span>No data</span>
                </div>
              </div>

              {/* Style toggle */}
              <button className="rlm-style-toggle" onClick={handleStyleToggle}>
                {mapStyle === 'rekkie' ? 'Dark' : 'REKKIE'}
              </button>
            </div>

            {/* Side panel */}
            <div className="rlm-panel">
              <div className="rlm-panel-tabs">
                <button
                  className={`rlm-tab ${activeTab === 'lifts' ? 'rlm-tab-active' : ''}`}
                  onClick={() => setActiveTab('lifts')}
                >
                  Lifts <span className="rlm-tab-count">{mapData.stats.liftsKnown}/{mapData.stats.liftsTotal}</span>
                </button>
                <button
                  className={`rlm-tab ${activeTab === 'trails' ? 'rlm-tab-active' : ''}`}
                  onClick={() => setActiveTab('trails')}
                >
                  Runs <span className="rlm-tab-count">{mapData.stats.trailsKnown}/{mapData.stats.trailsTotal}</span>
                </button>
              </div>

              <div className="rlm-panel-list">
                {activeTab === 'lifts' && (
                  <>
                    {[
                      { key: 'lifts-open', label: 'Open', items: liftGroups.open, color: STATUS_COLORS.open },
                      { key: 'lifts-closed', label: 'Closed', items: liftGroups.closed, color: STATUS_COLORS.closed },
                      { key: 'lifts-unknown', label: 'Unknown', items: liftGroups.unknown, color: STATUS_COLORS.unknown },
                    ].filter(g => g.items.length > 0).map(group => (
                      <div key={group.key} className="rlm-section">
                        <button
                          className="rlm-section-header"
                          onClick={() => toggleSection(group.key)}
                        >
                          <span className="rlm-section-chevron" style={{ transform: collapsedSections[group.key] ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▾</span>
                          <span className="rlm-section-dot" style={{ background: group.color }} />
                          <span className="rlm-section-label">{group.label}</span>
                          <span className="rlm-section-count">{group.items.length}</span>
                        </button>
                        {!collapsedSections[group.key] && group.items.map(lift => (
                          <div key={lift.id} className="rlm-list-item rlm-list-item-clickable" onClick={() => flyToLift(lift)}>
                            <div className="rlm-list-item-body">
                              <div className="rlm-list-item-name">{lift.name || lift.id}</div>
                              {lift.closureReason && lift.operationalStatus !== 'open' && (
                                <div className="rlm-list-item-meta">{lift.closureReason}</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                    {liftGroups.open.length === 0 && liftGroups.closed.length === 0 && liftGroups.unknown.length === 0 && (
                      <div className="rlm-empty-panel">No lift data available</div>
                    )}
                  </>
                )}

                {activeTab === 'trails' && (
                  <>
                    {[
                      { key: 'trails-groomed', label: 'Groomed', items: trailGroups.groomed, color: '#22c55e' },
                      { key: 'trails-ungroomed', label: 'Ungroomed', items: trailGroups.ungroomed, color: '#4ade80' },
                      { key: 'trails-unknown', label: 'Unknown', items: trailGroups.unknown, color: STATUS_COLORS.unknown },
                    ].filter(g => g.items.length > 0).map(group => (
                      <div key={group.key} className="rlm-section">
                        <button
                          className="rlm-section-header"
                          onClick={() => toggleSection(group.key)}
                        >
                          <span className="rlm-section-chevron" style={{ transform: collapsedSections[group.key] ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▾</span>
                          <span className="rlm-section-dot" style={{ background: group.color }} />
                          <span className="rlm-section-label">{group.label}</span>
                          <span className="rlm-section-count">{group.items.length}</span>
                        </button>
                        {!collapsedSections[group.key] && group.items.map(trail => (
                          <div key={trail.id} className="rlm-list-item rlm-list-item-clickable" onClick={() => flyToRun(trail.id)}>
                            <div className="rlm-list-item-body">
                              <div className="rlm-list-item-name">{trail.name || trail.id}</div>
                              <div className="rlm-list-item-meta">
                                {trail.colorName && <span className="rlm-difficulty">{trail.colorName}</span>}
                              </div>
                            </div>
                            <div className={`rlm-status-badge rlm-status-${trail.operationalStatus}`}>
                              {trail.operationalStatus}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                    {trailGroups.groomed.length === 0 && trailGroups.ungroomed.length === 0 && trailGroups.unknown.length === 0 && (
                      <div className="rlm-empty-panel">No trail data available</div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ResortLiveMapsPage;
